import React from "react";
import "./OptionCard.css";

/**
 * Selectable option card — the brand replacement for radio buttons. Surface-aware.
 * Used for difficulty (icon + name + description) and length (meta + name +
 * detail lines). Renders as a <button> for native keyboard/focus support.
 *
 * Props:
 *   selected:    bool
 *   onSelect:    () => void
 *   icon:        node (optional)        — e.g. difficulty bars
 *   iconColor:   css color (optional)   — semantic difficulty color
 *   meta:        string (optional)      — large emphasized text, e.g. "~15 min"
 *   name:        string                 — option title
 *   description: string (optional)
 *   detailLines: string[] (optional)    — small stacked detail rows (length)
 *   example:     node (optional)        — italic example with a top divider
 *   layout:      "stack" | "row"        — default "stack"
 */
const Check = () => (
  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor"
       strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const OptionCard = ({
  selected = false,
  onSelect,
  icon,
  iconColor,
  meta,
  name,
  description,
  detailLines,
  example,
  layout = "stack",
  className = "",
  ...rest
}) => (
  <button
    type="button"
    className={`gx-optcard gx-optcard--${layout} ${selected ? "is-selected" : ""} ${className}`}
    aria-pressed={selected}
    onClick={onSelect}
    {...rest}
  >
    <span className="gx-optcard-check" aria-hidden="true"><Check /></span>
    {icon && (
      <span className="gx-optcard-icon" style={iconColor ? { color: iconColor } : undefined}>
        {icon}
      </span>
    )}
    <span className="gx-optcard-text">
      {meta && <span className="gx-optcard-meta">{meta}</span>}
      <span className="gx-optcard-name">{name}</span>
      {description && <span className="gx-optcard-desc">{description}</span>}
      {detailLines && detailLines.length > 0 && (
        <span className="gx-optcard-details">
          {detailLines.map((line, i) => (
            <span key={i} className="gx-optcard-detail">{line}</span>
          ))}
        </span>
      )}
      {example && <span className="gx-optcard-example">{example}</span>}
    </span>
  </button>
);

export default OptionCard;
